import { supabase } from '@/lib/supabase';
import { getEdgeFunctionUrl } from '@/config/environment';

interface ProductSyncData {
  productId: string;
  name?: string;
  description?: string;
  hs_price_brl?: string;
  hs_price_usd?: string;
  hs_cost_of_goods_sold?: string;
  hs_product_type?: string;
  recurringbillingfrequency?: string;
  hs_sku?: string;
}

/**
 * Executa a sincronização usando a lógica equivalente à query SQL específica
 */
export const syncProductWithHubSpotRawQuery = async (planId: string): Promise<boolean> => {
  try {
    console.log('🔍 Executando sincronização com HubSpot para o plano:', { planId });

    // 1. Buscar dados do plano
    const { data: plan, error: planError } = await supabase
      .from('subscription_plans')
      .select(`
        id,
        name,
        description,
        service_type,
        billing_frequency,
        sku,
        subscription_plan_prices(
          currency,
          price_amount,
          cost_amount
        )
      `)
      .eq('id', planId)
      .single();

    if (planError || !plan) {
      console.error('❌ Erro ao buscar dados do plano:', planError);
      return false;
    }

    // 2. Buscar external_id do HubSpot
    const { data: externalId, error: externalIdError } = await supabase
      .from('external_ids')
      .select('external_id')
      .eq('entity_id', planId)
      .eq('entity_name', 'subscription_plans')
      .eq('system_name', 'hubspot')
      .single();

    if (externalIdError || !externalId?.external_id) {
      console.error('❌ External ID do HubSpot não encontrado:', externalIdError);
      console.log('ℹ️ Plano não sincronizado com HubSpot ainda. Pulando sincronização.');
      return true; // Return true to avoid error, just skip sync
    }

    // 3. Preparar dados para atualização
    const brlPrice = plan.subscription_plan_prices?.find(p => p.currency === 'BRL');
    const usdPrice = plan.subscription_plan_prices?.find(p => p.currency === 'USD');

    // Mapear service_type para hs_product_type
    // IMPORTANTE: Esta função deve ser IDÊNTICA às outras implementações
    const getHubSpotProductType = (serviceType: string): string => {
      console.log('🔍 Mapeando service_type:', serviceType, 'tipo:', typeof serviceType);
      
      // Normalizar o valor (trim e lowercase)
      const normalizedType = serviceType?.trim()?.toLowerCase();
      console.log('🔍 Valor normalizado:', normalizedType);
      
      if (normalizedType === 'licensing') {
        console.log('✅ Mapeado para license');
        return 'license';
      }
      if (normalizedType === 'material') {
        console.log('✅ Mapeado para material');
        return 'material';
      }
      if (normalizedType === 'program') {
        console.log('✅ Mapeado para program');
        return 'program';
      }
      if (normalizedType === 'professional_service') {
        console.log('✅ Mapeado para service');
        return 'service';
      }
      console.log('✅ Mapeado para service (default)');
      return 'service'; // Default para todos os outros casos
    };

    // Mapear billing_frequency para HubSpot
    const getRecurringBillingFrequency = (frequency: string): string => {
      const frequencyMap: Record<string, string> = {
        'month': 'monthly',
        'year': 'annually',
        'quarter': 'quarterly',
        'semiannual': 'semiannually',
        'one-time': 'one-time'
      };
      return frequencyMap[frequency] || 'monthly';
    };

    // 4. Construir o objeto de sincronização
    const syncData: ProductSyncData = {
      productId: externalId.external_id,
      name: plan.name,
      description: plan.description || '',
      hs_price_brl: brlPrice?.price_amount?.toString() || '',
      hs_price_usd: usdPrice?.price_amount?.toString() || '',
      hs_cost_of_goods_sold: brlPrice?.cost_amount?.toString() || usdPrice?.cost_amount?.toString() || '',
      hs_product_type: getHubSpotProductType(plan.service_type || 'licensing'),
      hs_sku: plan.sku || plan.id // Use SKU se disponível, senão use o ID como fallback
    };

    // Adicionar frequência de cobrança apenas se não for one-time
    if (plan.billing_frequency !== 'one-time') {
      syncData.recurringbillingfrequency = getRecurringBillingFrequency(plan.billing_frequency || 'month');
    }

    console.log('📊 Dados de preço e custo encontrados:', {
      brlPrice: brlPrice?.price_amount,
      brlCost: brlPrice?.cost_amount,
      usdPrice: usdPrice?.price_amount,
      usdCost: usdPrice?.cost_amount,
      hs_cost_of_goods_sold: brlPrice?.cost_amount || usdPrice?.cost_amount
    });

    return await makeHubSpotRequest(syncData);

  } catch (error) {
    console.error('❌ Erro geral na sincronização com HubSpot:', error);
    return false;
  }
};

/**
 * Função helper para fazer as requisições OPTIONS + POST para o HubSpot
 */
const makeHubSpotRequest = async (syncData: ProductSyncData): Promise<boolean> => {
  try {
    console.log('🌐 Iniciando requisições HTTP para edge function...', {
      productId: syncData.productId,
      url: getEdgeFunctionUrl('update-product-hubspot')
    });

    const edgeFunctionUrl = getEdgeFunctionUrl('update-product-hubspot');
    
    // Get the current user's session token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      console.error('❌ Nenhuma sessão ativa encontrada');
      return false;
    }
    const authToken = `Bearer ${session.access_token}`;

    // Primeiro, fazer a requisição OPTIONS
    console.log('🔄 Fazendo requisição OPTIONS...');
    const optionsResponse = await fetch(edgeFunctionUrl, {
      method: 'OPTIONS',
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json',
      },
    });

    if (!optionsResponse.ok) {
      console.error('❌ Erro na requisição OPTIONS:', optionsResponse.status, optionsResponse.statusText);
      return false;
    }

    console.log('✅ Requisição OPTIONS bem-sucedida');

    // Agora fazer a requisição POST
    console.log('🚀 Fazendo requisição POST para sincronização...');
    console.log('📤 Payload da requisição:', JSON.stringify(syncData, null, 2));
    
    const postResponse = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(syncData)
    });

    console.log('📥 Status da resposta:', postResponse.status, postResponse.statusText);

    if (!postResponse.ok) {
      const errorText = await postResponse.text();
      console.error('❌ Erro na requisição POST:', {
        status: postResponse.status,
        statusText: postResponse.statusText,
        responseBody: errorText,
        requestPayload: syncData
      });
      
      try {
        const errorJson = JSON.parse(errorText);
        console.error('❌ Detalhes do erro:', errorJson);
      } catch (e) {
        console.error('❌ Resposta não é JSON:', errorText);
      }
      
      return false;
    }

    const responseData = await postResponse.json();
    console.log('✅ Sincronização com HubSpot realizada com sucesso:', responseData);
    
    return true;

  } catch (error) {
    console.error('❌ Erro nas requisições HTTP:', error);
    return false;
  }
};